import { Restaurant } from '@maverick/entity';
import { BrowsableMenu } from '@maverick/cms';
import { ThemeProvider } from 'styled-components';
import { OutbackTheme } from '@maverick/themes';
import { useRouter } from 'next/router';
import { GetStaticPropsResult } from 'next';
import {
    BrowsableMenuPageProps,
    buildMenuMetaDescription,
    getEmptyNavigationItems,
    GetFooter,
    GetHeader,
    retrieveRestaurantMenu,
    retrieveRestaurantWithCalendar,
    retrieveRestaurantBySlug,
    REVALIDATE_DELAY,
} from 'src/utils/Utils';
import Head from 'next/head';
import { dictionaryServiceFactory } from 'lib/dictionary-service-factory';
import { Config } from 'src/Config';
import { CreateIsoDate, capitalizeOnlyFirstLetter, buildRestaurantMenuJsonLdForSlug } from '@maverick/utils';
import { GetNavigationItems } from '@maverick/sitecore';
import { ToastProvider } from '@maverick/ui';

export const getStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export async function getStaticProps(
    context: BrowsableMenuPageProps
): Promise<GetStaticPropsResult<BrowsableMenuPageProps>> {
    const genericRestaurantId = String(process.env.GHOST_RESTAURANT_ID);
    const params = context.params;

    try {
        let restaurantMenuPromise = null;
        let restaurantInfoPromise = null;
        if (params.restaurantSlug !== 'restaurant') {
            restaurantInfoPromise = retrieveRestaurantBySlug(params).then((restaurantInfo) =>
                retrieveRestaurantWithCalendar(restaurantInfo)
            );
            restaurantMenuPromise = restaurantInfoPromise.then((restaurantInfo) =>
                retrieveRestaurantMenu(restaurantInfo)
            );
        } else {
            restaurantMenuPromise = retrieveRestaurantMenu({ id: Number(genericRestaurantId) } as Restaurant);
        }

        const dictionaryService = dictionaryServiceFactory.create(Config.Brand);
        const dictionaryPromise = dictionaryService.fetchDictionaryData('en');

        const [dictionary, restaurantMenu, restaurantInfo] = await Promise.all([
            dictionaryPromise,
            restaurantMenuPromise,
            restaurantInfoPromise,
        ]);

        return {
            props: {
                params: {
                    ...params,
                    items: await GetNavigationItems(Config.Brand),
                    dictionary: dictionary,
                    restaurantMenu: restaurantMenu,
                    restaurantInfo: JSON.stringify(restaurantInfo) || null,
                    auth0Config: Config.Auth0,
                },
            },
            revalidate: REVALIDATE_DELAY,
        };
    } catch {
        return {
            props: {
                params: {
                    ...params,
                    items: getEmptyNavigationItems(),
                    dictionary: null,
                    restaurantMenu: null,
                    restaurantInfo: null,
                    auth0Config: Config.Auth0,
                },
            },
            revalidate: REVALIDATE_DELAY,
        };
    }
}

const MenuPage = (props: BrowsableMenuPageProps): React.JSX.Element => {
    const router = useRouter();
    const publicUrl = Config.Urls.Cms;
    const params = props.params;

    const parsedRestaurantInfo: Restaurant = JSON.parse(props.params.restaurantInfo || '');
    if (params.restaurantSlug !== 'restaurant') {
        parsedRestaurantInfo?.calendars?.forEach((calendar) => {
            if (calendar.ranges.length > 0) {
                calendar.ranges[0].end = CreateIsoDate(calendar.ranges[0].end);
                calendar.ranges[0].start = CreateIsoDate(calendar.ranges[0].start);
            }
        });
    }

    const locationString = parsedRestaurantInfo
        ? ` - ${capitalizeOnlyFirstLetter(parsedRestaurantInfo.city)}, ${
              parsedRestaurantInfo.state
          }, Updated for ${new Date().getFullYear()}`
        : '';
    const title = 'Outback Steakhouse Menu' + locationString;
    const description = buildMenuMetaDescription(parsedRestaurantInfo, "Steaks, ribs, seafood, Bloomin' Onion & more");

    const menuUrl = `${publicUrl}/menu/${props.params.restaurantSlug}`;
    const restaurantJsonLd = buildRestaurantMenuJsonLdForSlug(
        params.restaurantSlug ?? 'restaurant',
        parsedRestaurantInfo,
        menuUrl,
        'Steakhouse'
    );

    return (
        <ThemeProvider theme={OutbackTheme}>
            <ToastProvider>
                <Head>
                    <title>{title}</title>
                    <meta name='description' content={description} />
                    <link rel='icon' href={`${publicUrl}/favicon.ico`} />
                    <link rel='canonical' href={menuUrl} />
                    {restaurantJsonLd && (
                        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: restaurantJsonLd }} />
                    )}
                </Head>
                {GetHeader(params.items, params.auth0Config)}
                <div style={{ minHeight: 'calc(100vh - 100px)', backgroundColor: '#E5C8A2' }}>
                    <BrowsableMenu
                        router={router}
                        restaurantSlug={props.params.restaurantSlug}
                        restaurantMenu={props.params.restaurantMenu}
                        restaurantInfo={parsedRestaurantInfo}
                    ></BrowsableMenu>
                </div>

                {GetFooter(params.items)}
            </ToastProvider>
        </ThemeProvider>
    );
};

export default MenuPage;
